import readXlsxFile, { readSheetNames } from 'read-excel-file';
import { removeColumns, findEmptyColumns, isRowEmpty, isValueEmpty } from './helpers';
import type { ExtendedParseResult } from './types';
import { ParseWithoutSchemaOptions } from 'read-excel-file/types';

export const parseExcelFile = <T extends Record<string, unknown>>(
    file: File,
    complete: (results: ExtendedParseResult<T>) => void,
    error: (err: Error) => void,
    config?: {
        sheet?: number | string;
    },
) => {
    const processFile = async () => {
        try {
            const options: ParseWithoutSchemaOptions = {
                sheet: config?.sheet || 1,
                trim: true,
            };

            const sheetNames = await readSheetNames(file);
            const rows = await readXlsxFile(file, options);

            if (rows.length === 0) {
                throw new Error('Excel file is empty');
            }

            const headerRow = rows[0];
            const renamedHeaders: Record<string, string> = {};
            const headers = headerRow.map((header, index) => {
                const headerStr = header ? String(header).trim() : '';
                if (!headerStr) {
                    const renamed = `_${index + 1}`;
                    renamedHeaders[renamed] = '';
                    return renamed;
                }
                return headerStr;
            });

            const nonEmptyRows = rows.slice(1).filter((row) => !isRowEmpty(row));

            let data = nonEmptyRows.map((row) => {
                const obj: Record<string, unknown> = {};
                headers.forEach((header, index) => {
                    let value = row[index];
                    obj[header] = isValueEmpty(value) ? null : value;
                });
                return obj as T;
            });

            const columnsToRemove = [
                ...Object.entries(renamedHeaders)
                    .filter(([_, originalHeader]) => !originalHeader.trim())
                    .map(([renamedHeader]) => renamedHeader),
                ...headers.filter((field) => !field.trim()),
            ];

            data = removeColumns(data, columnsToRemove);

            const emptyColumnsWithHeaders = findEmptyColumns(data, columnsToRemove);

            const updatedFields = headers.filter(
                (field) => field.trim() && !columnsToRemove.includes(field),
            );

            const result: ExtendedParseResult<T> = {
                data,
                errors: [],
                meta: {
                    renamedHeaders,
                    fields: updatedFields,
                    emptyColumns: emptyColumnsWithHeaders,
                    sheetsCount: sheetNames.length,
                },
            };

            complete(result);
        } catch (err) {
            error(err as Error);
        }
    };

    processFile();
};
